import { InjectionKey } from '@pikpak_web_player/components/file-manager/fileManager'
import { computed, type ComputedRef, inject, type Ref } from 'vue'

export type ProvidesMap = {
  currentFile: ComputedRef<PPFile>
  lastFileRef: Ref<PPFile | undefined>
  fileManagerEl: Ref<HTMLElement | undefined>
  previewBarVisible: Ref<boolean>
  isTagFile: ComputedRef<boolean>
  fullscreenStatus: Ref<boolean>
  isFreeze: Ref<boolean>
  isMobile: Ref<boolean>
  isDesktop: boolean
  isPin: boolean
  scene: ComputedRef<string>
  lockSize: Ref<
    | {
        width: number
        height: number
      }
    | undefined
  >

  currentFileState: {
    playStatus: boolean
    progress: number
    waiting: boolean
    isError: boolean
  }
  setCurrentFileState: (playStatus: boolean, waiting: boolean, progress: number, isError: boolean) => void

  getFileById: (id: string) => PPFile | null
  getFileByIndex: (index: number) => PPFile | undefined
  getIsLastFile: (id: string) => boolean
  getIsOnlyFile: () => boolean
  getMediaRecord?: (fileId: string) => Promise<{ videoId: string; position: number; percentage: number } | null>
}

export const useIsFreeze = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.isFreeze.value || false)
}

export const useCurrentFile = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.currentFile.value)
}

export const useLastFile = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.lastFileRef.value)
}

export const useFileManagerEl = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.fileManagerEl.value || null)
}

export const useFullscreenStatus = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.fullscreenStatus.value || false)
}

export const usePreviewBarVisible = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.previewBarVisible.value || false)
}

export const useIsTagFile = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.isTagFile.value || false)
}

export const useIsMobile = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.isMobile.value || false)
}

export const useIsDesktop = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.isDesktop || false)
}

export const useIsPin = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.isPin || false)
}

export const useScene = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.scene?.value || '')
}

export const useLockSize = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(() => provides?.lockSize.value)
}

export const useCurrentFileState = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)
  return computed(
    () =>
      provides?.currentFileState || {
        playStatus: false,
        progress: 0,
        waiting: false,
        isError: false,
      },
  )
}

export const useProvidesMethod = () => {
  const provides = inject<ProvidesMap>(InjectionKey.FILE_MANAGER_PROVIDES)

  return {
    getFileById: provides?.getFileById || (() => null),
    getFileByIndex: provides?.getFileByIndex || (() => undefined),
    getIsLastFile: provides?.getIsLastFile || (() => true),
    getIsOnlyFile: provides?.getIsOnlyFile || (() => false),
    getMediaRecord: async (fileId: string) => {
      if (provides?.getMediaRecord) {
        return await provides.getMediaRecord(fileId)
      }
      return null
    },
    setCurrentFileState: provides?.setCurrentFileState || (() => undefined),
  }
}
